import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState("");
  const [mfaStep, setMfaStep] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, mfa_code: mfa || undefined });
      if (data.mfa_required) { setMfaStep(true); setLoading(false); return; }
      setUser(data.user);
      toast.success("Welcome back");
      const home = (data.user.permissions || []).includes("devices:view") || data.user.role === "mimoit_admin" ? "/dashboard" : "/tickets";
      nav(home);
    } catch (e2) {
      setErr(apiErr(e2));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center grid-bg" style={{ background: "#0B0E14" }}>
      <div className="w-full max-w-sm mx-4 fade-up">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src="/brand/mimoit-icon.png" alt="mimoIT" className="w-12 h-12 object-contain" />
          <div>
            <div className="font-heading font-extrabold text-xl text-slate-100 tracking-tight">mimoIT<span style={{color:"#00F0FF"}}> Control</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Remote Monitoring & Management</div>
          </div>
        </div>

        <form onSubmit={submit} className="card-surface rounded-xl p-6 space-y-4 shadow-2xl">
          <div>
            <h1 className="font-heading font-bold text-lg text-slate-100">Sign in</h1>
            <p className="text-[13px] text-slate-500">Technician & administrator console</p>
          </div>

          {!mfaStep ? (
            <>
              <div>
                <label className="text-[12px] font-medium text-slate-400">Email</label>
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-500" />
              </div>
              <div>
                <label className="text-[12px] font-medium text-slate-400">Password</label>
                <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-500" />
              </div>
            </>
          ) : (
            <div>
              <label className="text-[12px] font-medium text-slate-400 flex items-center gap-1"><ShieldCheck size={13} /> Authenticator code</label>
              <input data-testid="login-mfa" value={mfa} onChange={(e) => setMfa(e.target.value)} autoFocus placeholder="000000" maxLength={6}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-lg font-mono tracking-[0.3em] text-center text-slate-100 outline-none focus:border-cyan-500" />
            </div>
          )}

          {err && <div data-testid="login-error" className="text-[13px] text-rose-400 bg-rose-950/40 border border-rose-900 rounded-md px-3 py-2">{err}</div>}

          <button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold text-slate-950 disabled:opacity-60 transition-transform hover:scale-[1.01]"
            style={{ background: "linear-gradient(135deg,#00F0FF,#38BDF8)" }}>
            {loading && <Loader2 size={15} className="animate-spin" />} {mfaStep ? "Verify" : "Sign in"}
          </button>
          <p className="text-[11px] text-slate-600 text-center">Protected by MFA · session & IP logging · brute-force protection</p>
        </form>
      </div>
    </div>
  );
}
